<script setup lang="ts">
import { computed } from "vue";

import { linkDragState } from "@/composables/linkDrag";
import { linkViaPoint } from "@/math/QuadraticSpline";
import { useBoardSizeStore } from "@/store/boardSize";
import { useZoomStore } from "@/store/zoom";

const path = computed(() => {
  const s = linkDragState.value;
  if (!s) return "";
  const via = linkViaPoint(s.anchor, s.pointer);
  return `M ${s.anchor.x} ${s.anchor.y} Q ${via.x} ${via.y} ${s.pointer.x} ${s.pointer.y}`;
});

// Measure the live LinkLayer to get true stroke scale (via 1600×900 viewBox).
const strokeWidth = computed(() => {
  // Reactive deps so recompute on resize/zoom.
  const size = useBoardSizeStore().board.size;
  const zoom = useZoomStore().factor;
  if (size.x <= 0 || size.y <= 0 || zoom <= 0) return 2;
  const el = document.querySelector<SVGSVGElement>(".link-layer");
  if (!el) return 2;
  const rect = el.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return 2;
  return Math.sqrt((rect.width / 1600) * (rect.height / 900));
});

const maskId = "link-drag-line-source-mask";
</script>

<template>
  <Teleport to="body">
    <svg v-if="linkDragState" class="link-drag-line" aria-hidden="true">
      <defs v-if="linkDragState.sourceRect">
        <mask :id="maskId" maskUnits="userSpaceOnUse">
          <rect x="0" y="0" width="100%" height="100%" fill="white" />
          <rect
            :x="linkDragState.sourceRect.left"
            :y="linkDragState.sourceRect.top"
            :width="linkDragState.sourceRect.right - linkDragState.sourceRect.left"
            :height="linkDragState.sourceRect.bottom - linkDragState.sourceRect.top"
            fill="black"
          />
        </mask>
      </defs>
      <path
        :d="path"
        :stroke="linkDragState.color"
        :stroke-width
        :mask="linkDragState.sourceRect ? `url(#${maskId})` : undefined"
      />
    </svg>
  </Teleport>
</template>

<style scoped lang="scss">
@use "@/styles/z-index";

.link-drag-line {
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh;
  pointer-events: none;
  z-index: z-index.$popup-menu;

  path {
    fill: none;
  }
}
</style>
